import { createSelector } from '@reduxjs/toolkit';
import { AgendasTypes, IAgenda } from '../constants/types';

/**
 * SHER-233: `agenda.tsx` used to hold a SINGLE module-level `createSelector`
 * instance shared across every `type` argument (`selectAgendas(state, type)`).
 * Reselect's default memoization only remembers the last argument tuple, so
 * interleaving calls with different `type`s — exactly what
 * `MySchedule.tsx` does, calling `useAgendas(AgendasTypes.CONSULTATION)` then
 * `useAgendas(AgendasTypes.SURGERY)` in the same render — evicted each
 * other's cached result. From then on every call recomputed and returned a
 * brand-new array reference, even when `state.hospital.data.agendas` hadn't
 * changed. That defeated `useSelector`'s referential-equality bailout and fed
 * a continuous re-render/re-fetch loop on the Consultation agenda page.
 *
 * Fix: give each `type` its own single-entry memoized selector, built lazily
 * once per type and cached here (not rebuilt per render/per call).
 * Interleaved calls with different types can then never evict each other.
 *
 * Pulled into its own module (rather than living inline in `agenda.tsx`) so
 * it can be unit-tested without dragging in `agenda.tsx`'s service/action
 * imports, whose transitive chain currently fails to parse under this repo's
 * Jest config (pre-existing `jose` ESM gap, not something this ticket fixes).
 */
const selectorsByType = new Map<AgendasTypes, (state: any) => IAgenda[] | null>();

export function getAgendasSelector(type: AgendasTypes) {
    let selector = selectorsByType.get(type);
    if (!selector) {
        selector = createSelector(
            (state: any) => state.hospital.data.agendas,
            (agendas: IAgenda[] | null) => {
                if (!agendas) return null;
                return agendas.filter((agenda: IAgenda) => agenda.type === type);
            }
        );
        selectorsByType.set(type, selector);
    }
    return selector;
}
